import { Injectable, Optional } from '@angular/core';
import { ActivatedRouteSnapshot, Router } from '@angular/router';
import { CitrusAdService, ProductDetails, ProductService } from '@woolworthsnz/product';
import { HOME_PATH } from '@woolworthsnz/shop/routes';
import { FeatureService, LoggingService } from '@woolworthsnz/styleguide';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { ContextResponse, ProductDetailsResponse } from '@woolworthsnz/trader-api';
import { SiteDetail, StoresService } from '@woolworthsnz/store-locator';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Injectable({
	providedIn: 'root',
})
export class ProductDetailsResolver {
	// Google Merchant Hosted Local Storefront feature flag
	featureState$ = this.featureService.state$.pipe(takeUntilDestroyed());
	private isMhlsEnabled = false;

	constructor(
		private productService: ProductService,
		private router: Router,
		private citrusAdService: CitrusAdService,
		private featureService: FeatureService,
		private storesService: StoresService,
		@Optional() private loggingService: LoggingService
	) {
		this.featureState$.subscribe(() => {
			this.isMhlsEnabled = this.featureService.isFeatureEnabled(
				ContextResponse.EnabledFeaturesEnum.MerchantHostedLocalStorefront
			);
		});
	}

	resolve(route: ActivatedRouteSnapshot): Observable<ProductDetails> {
		if (!route.params.id && !route.queryParams.stockcode) {
			// We may be coming from an old route, so redirect to the new route
			this.router.navigate([route.routeConfig?.path, route.queryParams.stockcode]);
		}

		const stockCode = route.params.id ? route.params.id : route.queryParams.stockcode;
		const physicalStoreId = route.queryParams.store && this.isMhlsEnabled ? route.queryParams.store : undefined;
		const onlineStoreId = route.queryParams.ostore && this.isMhlsEnabled ? route.queryParams.ostore : undefined;

		let physicalStoreInfo$ = of<SiteDetail | undefined>(undefined);
		// Only load the physical store details if the online store code wasn't specified in the URL query param
		if (!onlineStoreId && physicalStoreId) {
			physicalStoreInfo$ = this.storesService.getStoreInfo(physicalStoreId).pipe(
				map((res) => (res && res.length ? res[0] : undefined)),
				catchError(() => of(undefined))
			);
		}

		return physicalStoreInfo$.pipe(
			switchMap((physicalStoreInfo: SiteDetail | undefined) => {
				// If the online store id query param is provided, use that instead of the mapped store id received from store locator
				const storeId = onlineStoreId || physicalStoreInfo?.site?.extra1;

				// Load product details
				return this.productService.getProductDetail(stockCode, storeId).pipe(
					map((productDetailsResponse: ProductDetailsResponse) => {
						const productDetails: ProductDetails = {
							...productDetailsResponse,
							storeInfo: physicalStoreInfo,
						};
						if (this.citrusAdService.state?.sku === productDetails?.sku) {
							productDetails.adId = this.citrusAdService.state?.adId;
						}
						this.citrusAdService.clearState();
						return productDetails;
					}),
					catchError((error) => {
						this.loggingService.trackException(error);
						if (error.status === 500) {
							return of({ apiFailed: true });
						}
						this.router.navigateByUrl(HOME_PATH);
						return of(error);
					})
				);
			})
		);
	}
}
